import React, { useState, useEffect } from 'react';
import _t from 'counterpart';
import {
  CButton,
  CModalBody,
  CModalHeader,
  CModal,
  CModalFooter,
  CModalTitle,
  CForm,
  CLabel,
  CFormGroup,
  CSelect,
  CAlert,
  CInput,
} from '@coreui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { updateWallet, loadGroups, loadMt5Groups, loadWarehouseAndFixAdapters } from '../../services/BackendService';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { isGatewayWallet, IWallet } from '../wallets/types';
import { GroupPlatform, IGroup, IMt5Group } from '../groups/types';

export function OrderRoutingAdapterSelector(props: { onChange: any; value: string | undefined }) {
  const { onChange, value } = props;

  const { isLoading, error, data } = useQuery<any, Error>(['order-routing-adapters'], async () => {
    const data = await loadWarehouseAndFixAdapters();
    return data;
  });

  useEffect(() => {
    if (data && (value === '' || !value)) {
      onChange(data![0].name);
    }
  }, [data, value, onChange]);

  if (isLoading) {
    return (
      <CSelect id="nf-type" name="nf-type" disabled>
        <option>{_t('global.loading')}</option>
      </CSelect>
    );
  }

  if (error) {
    return <span>{error.message}</span>;
  }

  return (
    <CSelect id="nf-type" name="nf-type" onChange={(e: any) => onChange(e.target.value)} value={value}>
      {data.map((o: any) => (
        <option value={o.id} key={`adapter-${o.id}`}>
          {o.name}
        </option>
      ))}
    </CSelect>
  );
}

/**
 * MT5 group picker. Separate from {@link OrderGroupsSelector} on purpose: MT5 groups come from the
 * MT5 server rather than the local group table, so they are keyed by name (there is no local id)
 * and carry MT5's own currency.
 */
export function Mt5GroupsSelector(props: { onChange: (group: string | undefined) => void; value: string | undefined }) {
  const { onChange, value } = props;
  const { isLoading, error, data } = useQuery<Array<IMt5Group>, Error>(['mt5-groups'], () => loadMt5Groups());

  if (isLoading) {
    return (
      <CSelect id="nf-mt5-group" name="nf-mt5-group" disabled>
        <option>{_t('global.loading')}</option>
      </CSelect>
    );
  }

  if (error) {
    return <span>{error.message}</span>;
  }

  if (!data || data.length === 0) {
    return <span className="text-danger">{_t('groups.mt5-none-available')}</span>;
  }

  // Also re-selects when the stored group is no longer on the MT5 server (renamed/removed there):
  // leaving a value that matches no option would render as blank and submit a stale name.
  if (!value || !data.some((g) => g.group === value)) {
    onChange(data[0].group);
  }

  return (
    <CSelect id="nf-mt5-group" name="nf-mt5-group" onChange={(e: any) => onChange(e.target.value)} value={value ?? ''}>
      {data.map((g) => (
        <option value={g.group} key={g.group}>
          {g.group} ({g.currency})
        </option>
      ))}
    </CSelect>
  );
}

export function OrderGroupsSelector(props: {
  onChange: any;
  value: number | undefined;
  currency: string;
  platform?: GroupPlatform;
}) {
  const { onChange, value, currency, platform = GroupPlatform.Wintrado } = props;

  // Keyed by platform: MT5 and Wintrado groups are disjoint lists and must not share a cache entry.
  const { isLoading, error, data } = useQuery<Array<IGroup>, Error>(['order-groups', platform], () =>
    loadGroups(platform)
  );

  if (isLoading) {
    return (
      <CSelect id="nf-group" name="nf-group" disabled>
        <option>{_t('global.loading')}</option>
      </CSelect>
    );
  }

  if (error) {
    return <span>{error.message}</span>;
  }

  if (!value) {
    onChange(data![0]?.id);
  }

  const handleOnChange = (e: any) => {
    onChange(Number(e.target.value));
  };

  return (
    <CSelect id="nf-type" name="nf-type" onChange={handleOnChange} value={value}>
      {data!.map((o: any) => (
        <option value={o.id} key={o.id} disabled={currency !== o.depositCurrency}>
          {o.name} ({o.depositCurrency})
        </option>
      ))}
    </CSelect>
  );
}

function CustomerWalletPopup(props: { wallet: IWallet; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { wallet, onClose } = props;
  const isMt5 = !isGatewayWallet(wallet);
  const [groupId, setGroupId] = useState(wallet.group?.id);
  // MT5 groups are identified by the name the MT5 server reports, not by a local id.
  const [mt5Group, setMt5Group] = useState<string | undefined>(wallet.group?.name);
  const [leverage, setWalletLeverage] = useState<string>('');
  const update = useMutation<any, Error, any>(
    (id: string) =>
      updateWallet(id, isMt5 ? { mt5Group, leverage: Number(leverage) } : { groupId, leverage: Number(leverage) }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customer-wallets']);
        onClose();
      },
    }
  );

  useEffect(() => {
    if (!leverage) {
      setWalletLeverage(wallet.leverage.toString());
    }
  }, [wallet.leverage]);

  const setWalletLeverageWrapper = (event: any) => {
    setWalletLeverage(event.target.value);
  };

  const mutateWallet = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(wallet.id);
  };

  return (
    <CModal show onClose={onClose} size="sm">
      <CModalHeader>
        <CModalTitle>{_t('wallet-modal.title')}</CModalTitle>
      </CModalHeader>
      <CForm onSubmit={mutateWallet}>
        <CModalBody>
          {update.error && <CAlert color="danger">{update.error.message}</CAlert>}
          <CFormGroup>
            <CLabel htmlFor="nf-group">{_t('wallet-modal.group')}</CLabel>
            {isMt5 ? (
              <Mt5GroupsSelector onChange={setMt5Group} value={mt5Group} />
            ) : (
              <OrderGroupsSelector
                onChange={setGroupId}
                value={groupId}
                currency={wallet.currency}
                platform={GroupPlatform.Wintrado}
              />
            )}
          </CFormGroup>
          <CFormGroup>
            <CLabel htmlFor="nf-leverage">{_t('customer.wallets.set-wallet-leverage')}</CLabel>
            <CInput
              id="nf-leverage"
              name="nf-leverage"
              placeholder="300"
              value={leverage}
              onChange={setWalletLeverageWrapper}
            />
          </CFormGroup>
        </CModalBody>
        <CModalFooter>
          <ButtonWithLoader
            isLoading={update.isLoading}
            type="submit"
            buttonColor="primary"
            spinnerColor="secondary"
            title={_t.translate('global.confirm')}
            className="mr-2"
          />
          <CButton color="light" variant="outline" onClick={onClose} disabled={update.isLoading}>
            {_t('global.cancel')}
          </CButton>
        </CModalFooter>
      </CForm>
    </CModal>
  );
}

export default CustomerWalletPopup;
